/**
 * ContactContextPane - the facts of the contact, always to hand.
 *
 * Type, status, onboarding, the email and phone and address, when the record
 * was made and last touched: all of it read off the Details tab before, as
 * three `FactTile` grids under the notes card. Which meant it left the screen
 * the moment someone opened Matters or Documents - exactly when "who is this,
 * are they still active, what is their email" gets asked. As a pane it stays
 * put.
 *
 * IT STRETCHES. The pane resizes from 260 to 720, so the two-word answers are
 * an `auto-fit` grid rather than a fixed column count: one column in a narrow
 * dock, two when it is pulled wide.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCalendarBlankLight,
  PiCircleDashedLight,
  PiClockClockwiseLight,
  PiEnvelopeLight,
  PiMapPinLight,
  PiPhoneLight,
  PiSealCheckLight,
  PiTagLight,
  PiUserLight,
} from 'react-icons/pi';
import Badge from '../../shared/TailwindComponents/Badge';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import { Field, Well } from '../../shared/brand/Field';
import { contactValue } from './ContactValue';
import { formatDateTime } from '../../../utils/functions';
import { ContextSection } from '../../shared/brand/ContextPane';

interface Props {
  client: any;
  /** The resolved label for `type_id` - the page owns the type list. */
  typeLabel: string;
  /**
   * The colour that label gets, grouped by the PARENT type - the same badge
   * the contacts table and the detail hero render, so one contact reads the
   * same colour in the list, in the header and here. See `contactTypeBadge`.
   */
  typeColor?: BadgeColor;
}

const ContactContextPane: React.FC<Props> = ({ client, typeLabel, typeColor = 'gray' }) => {
  const { t } = useTranslation();

  if (!client) return null;

  // `entity_kind` is the backend's word for it (feature/final-revamp-backend);
  // `is_individual` is the field that has always been there.
  const isCompany = client.entity_kind
    ? client.entity_kind !== 'person'
    : client.is_individual === false;
  const isOnboarded = client.is_onboarded ?? client.client_metadata?.IsOnboarded;
  const hasContactDetails =
    client.contact_email || client.contact_phone || client.address ||
    (isCompany && (client.contact_person_name || client.contact_person_email));

  return (
    <div className="flex flex-col h-full min-h-0 overflow-y-auto custom-scrollbar-main px-3 py-3 space-y-4">
      {/* Identity - the two-word answers, side by side. */}
      <div>
        <ContextSection>{t('Identity')}</ContextSection>
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}
        >
          <Field icon={<PiTagLight />} label={t('Type')}>
            <Badge color={typeColor}>{typeLabel}</Badge>
          </Field>
          <Field icon={<PiCircleDashedLight />} label={t('Status')}>
            <Badge color={client.is_active ? 'green' : 'gray'}>
              {client.is_active ? t('Active') : t('Inactive')}
            </Badge>
          </Field>
          <Field icon={<PiSealCheckLight />} label={t('Onboarded')}>
            <Badge color={isOnboarded ? 'green' : 'yellow'}>
              {isOnboarded ? t('Yes') : t('No')}
            </Badge>
          </Field>
        </div>
      </div>

      {/* Contact - how to reach them. */}
      {hasContactDetails && (
        <div>
          <ContextSection>{t('Contact')}</ContextSection>
          <Well className="space-y-3">
            {client.contact_email && (
              <Field
                icon={<PiEnvelopeLight />}
                label={isCompany ? t('Organization email') : t('Email')}
              >
                {contactValue(client.contact_email)}
              </Field>
            )}
            {client.contact_phone && (
              <Field icon={<PiPhoneLight />} label={t('Phone')}>{contactValue(client.contact_phone)}</Field>
            )}
            {isCompany && client.contact_person_name && (
              <Field
                icon={<PiUserLight />}
                label={t('Contact person')}
              >
                {contactValue(client.contact_person_name)}
              </Field>
            )}
            {isCompany && client.contact_person_email && (
              <Field
                icon={<PiEnvelopeLight />}
                label={t('Contact person email')}
              >
                {contactValue(client.contact_person_email)}
              </Field>
            )}
            {client.address && (
              <Field icon={<PiMapPinLight />} label={t('Address')}>{contactValue(client.address)}</Field>
            )}
          </Well>
        </div>
      )}

      {/* Record - when it was made, when it last moved. */}
      <div>
        <ContextSection>{t('Record')}</ContextSection>
        <Well className="space-y-3">
          <Field
            icon={<PiCalendarBlankLight />}
            label={t('Created')}
            value={client.created_at ? formatDateTime(client.created_at) : undefined}
            emptyText="-"
          />
          {client.updated_at && (
            <Field
              icon={<PiClockClockwiseLight />}
              label={t('Last updated')}
              value={formatDateTime(client.updated_at)}
            />
          )}
        </Well>
      </div>
    </div>
  );
};

export default ContactContextPane;
